import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { EMPTY_REQUIREMENTS, RequirementsEditor, requirementsPayload, type RequirementsValue } from '../../components/RequirementsEditor.tsx';
import { useCatalog } from '../../catalog.ts';
import { BriefingForm } from '../../components/BriefingForm.tsx';
import { EMPTY_DRAFT, briefingPayload, draftFrom, type BriefingDraft, type MissionTemplate } from '../../briefing.ts';
import type { Establishment, Mission, Organization } from '../../types.ts';

export function NewMission() {
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [reqs, setReqs] = useState<RequirementsValue>(EMPTY_REQUIREMENTS);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [establishmentId, setEstablishmentId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('23:00');
  const [hourlyRate, setHourlyRate] = useState('18');
  const [slotsCount, setSlotsCount] = useState('1');
  const [briefing, setBriefing] = useState<BriefingDraft>(EMPTY_DRAFT);
  const [templates, setTemplates] = useState<MissionTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ organizations: Organization[] }>('/organizations/mine').then(async (res) => {
      const org = res.organizations[0];
      setOrganizationId(org.id);
      api.get<{ templates: MissionTemplate[] }>(`/missions/templates?organizationId=${org.id}`).then((t) => setTemplates(t.templates)).catch(() => undefined);
      const est = await api.get<{ establishments: Establishment[] }>(`/organizations/${org.id}/establishments`);
      setEstablishments(est.establishments);
      if (est.establishments[0]) setEstablishmentId(est.establishments[0].id);
    });
  }, []);

  const minutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  // Décaler le début de la mission décale le planning : les étapes gardent leur écart avec le début.
  function changeStart(next: string) {
    if (next && startTime) {
      const delta = minutes(next) - minutes(startTime);
      setBriefing((b) => ({
        ...b,
        schedule: b.schedule.map((item) => {
          if (!item.time) return item;
          const t = (((minutes(item.time) + delta) % 1440) + 1440) % 1440;
          return { ...item, time: `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}` };
        }),
      }));
    }
    setStartTime(next);
  }

  function loadTemplate(id: string) {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const d = t.data;
    setTitle(d.title);
    setDescription(d.description ?? '');
    setHourlyRate(String(d.hourlyRateCents / 100));
    setSlotsCount(String(d.slotsCount));
    setReqs({
      coversCount: d.coversCount ? String(d.coversCount) : '',
      teamMode: d.teamMode ?? '',
      requirements: Object.fromEntries(d.requirements.map((r) => [r.itemId, { importance: r.importance, minLevel: r.minLevel ?? '' }])),
    });
    setBriefing(draftFrom(d.briefing, startTime));
  }

  async function onSubmit() {
    if (!organizationId || !establishmentId || !title || !date) return;
    setSubmitting(true);
    setError(null);
    try {
      const startsAt = new Date(`${date}T${startTime}:00`).toISOString();
      // Un service qui finit à 02 h 00 se termine le lendemain.
      const end = new Date(`${date}T${endTime}:00`);
      if (endTime <= startTime) end.setDate(end.getDate() + 1);
      const endsAt = end.toISOString();
      const missionRes = await api.post<{ mission: Mission }>('/missions', {
        organizationId,
        establishmentId,
        title,
        description: description || undefined,
        startsAt,
        endsAt,
        hourlyRateCents: Math.round(Number(hourlyRate) * 100),
        slotsCount: Math.max(1, Number(slotsCount) || 1),
        ...requirementsPayload(reqs),
        briefing: briefingPayload(briefing, startTime),
      });
      await api.post(`/missions/${missionRes.mission.id}/publish`);
      if (saveTemplate && templateName.trim()) {
        // Un modèle non enregistré (nom déjà pris…) ne doit pas empêcher la mission d'être publiée.
        await api.post('/missions/templates', { organizationId, name: templateName.trim(), fromMissionId: missionRes.mission.id }).catch(() => undefined);
      }
      navigate(`/resto/missions/${missionRes.mission.id}`);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(
        code === 'INVALID_SCHEDULE' ? 'Une étape du planning est invalide : elle doit tomber pendant la mission (entre le début et la fin).'
        : code === 'INVALID_CHECKLIST' ? 'Un point de la check-list est invalide.'
        : code === 'INVALID_BREAK' ? 'La pause doit durer entre 0 et 240 minutes.'
        : code === 'INVALID_BRIEFING_TIME' ? 'Le brief doit être placé entre 0 et 12 heures avant le début.'
        : code.startsWith('INVALID_BRIEFING') ? 'Un des textes de la fiche est trop long.'
        : 'Impossible de créer la mission.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell">
      <TopBar title="Nouvelle mission" back />
      <div className="screen-content">
        {error && <div className="error-banner">{error}</div>}

        {templates.length > 0 && (
          <div>
            <label className="field-label" htmlFor="template">Partir d’un modèle</label>
            <select id="template" className="input" value={templateId} onChange={(e) => loadTemplate(e.target.value)}>
              <option value="">Nouvelle mission vierge</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="title">Intitulé</label>
          <input id="title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Service samedi soir" />
        </div>

        {establishments.length > 1 && (
          <div>
            <label className="field-label" htmlFor="establishment">Établissement</label>
            <select id="establishment" className="input" value={establishmentId} onChange={(e) => setEstablishmentId(e.target.value)}>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>{est.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="description">Description (optionnel)</label>
          <textarea id="description" className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div>
          <label className="field-label" htmlFor="date">Date</label>
          <input id="date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="start">Début</label>
            <input id="start" type="time" className="input" value={startTime} onChange={(e) => changeStart(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="end">Fin</label>
            <input id="end" type="time" className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="rate">Taux horaire (€)</label>
            <input id="rate" type="number" min="1" step="0.5" className="input" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="slots">Nombre d’extras</label>
            <input id="slots" type="number" min="1" max="50" step="1" className="input" value={slotsCount} onChange={(e) => setSlotsCount(e.target.value)} />
          </div>
        </div>

        {catalog && <RequirementsEditor catalog={catalog} value={reqs} onChange={setReqs} />}

        <BriefingForm value={briefing} onChange={setBriefing} />

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, fontWeight: 700 }}>
            <input type="checkbox" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} /> Enregistrer comme modèle pour une prochaine mission
          </label>
          {saveTemplate && <input className="input" aria-label="Nom du modèle" maxLength={80} placeholder="Nom du modèle (ex. Banquet 80 couverts)" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />}
        </div>

        <button className="btn btn-primary" onClick={onSubmit} disabled={submitting || !title || !date}>
          {submitting ? 'Publication…' : 'Publier la mission'}
        </button>
      </div>
    </div>
  );
}
